package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.info
import com.siriusxm.cmc.utils.copyOrBuild
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.AppConfigAttribute
import unifiedcontent.aggregator.AppConfigFeatureFlag
import unifiedcontent.aggregator.AppConfigFeatureFlagsDocument
import unifiedcontent.aggregator.FeatureFlagUpdate
import unifiedcontent.aggregator.FeatureFlagsUpdate
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

/**
 * Feature flags management interface with tabbed navigation.
 *
 * Provides a dual-view interface for managing feature flags:
 * - **Current Tab**: Editable interface for viewing and modifying the current feature flag configuration
 * - **History Tab**: Read-only view of historical feature flag changes
 *
 * This component serves as the main entry point for feature flag management in the aggregator,
 * allowing users to both configure current flags and review past changes.
 *
 * @param aggregatorService The aggregator service instance for API access
 * @param editable Whether the current flags should be editable (admin-only)
 */
@Composable
fun ServiceViewContext.featureFlags(aggregatorService: AggregatorService, editable: Boolean) {
    serviceContentView({
        title = "Feature Flags"
        breadcrumbs {
            crumb("Aggregator", "aggregator") {
                crumb("Features", null)
            }
        }
    }) {
        tabView {
            tab("Current") {
                featureFlagsEditor(aggregatorService, editable)
            }
            tab("History") {
                featureFlagHistory(aggregatorService)
            }
        }
    }
}

/**
 * Feature flags editor component with save/reset functionality.
 *
 * Provides an interactive interface for modifying feature flags:
 * - Displays all feature flags with their current states
 * - Allows toggling flags on/off (if editable)
 * - Shows flag names, descriptions, and custom attributes
 * - Edits each attribute with a widget chosen from its declared type (UCA-979), see [AttributeEditor]
 * - Requires a deployment description when saving changes
 * - Includes a confirmation dialog before submitting changes
 *
 * Changes are tracked locally and only submitted when the user clicks Save.
 * Users can reset changes to discard all pending modifications.
 *
 * Two pieces of local state track edits:
 * - `updates` holds the complete pending value object (the `enabled` key plus every attribute)
 *   for each flag that differs from the saved document. It is what gets saved.
 * - `drafts` holds the raw text typed into text, number and JSON attribute fields. Text that
 *   fails validation stays in `drafts` with an error and never reaches `updates`; while any
 *   draft has an error, Save is disabled.
 *
 * @param aggregatorService The aggregator service instance for fetching and updating flags
 * @param editable Whether flags can be modified (typically true for admins only)
 */
@Composable
private fun featureFlagsEditor(aggregatorService: AggregatorService, editable: Boolean) {
    var featureFlags by remember { mutableStateOf<AppConfigFeatureFlagsDocument?>(null) }
    var updates by remember { mutableStateOf<Map<String, JsonObject>>(emptyMap()) }
    var drafts by remember { mutableStateOf<Map<AttributeRef, AttributeDraft>>(emptyMap()) }
    var description by remember { mutableStateOf<String?>(null) }
    var showConfirmation by remember { mutableStateOf(false) }

    LaunchedEffect(true) {
        featureFlags = aggregatorService.clientApi.getFeatureFlags()
    }

    suspend fun performSave() {
        if (updates.isNotEmpty()) {
            try {
                aggregatorService.clientApi.updateFeatureFlags(
                    FeatureFlagsUpdate(
                        flags = updates.map { (key, value) ->
                            val isEnabled = value["enabled"]?.jsonPrimitive?.booleanOrNull
                            FeatureFlagUpdate(
                                key, isEnabled ?: false,
                                value.filterKeys { it != "enabled" })
                        },
                        deploymentDescription = description
                    )
                )
                // Fold the saved changes into the local document so the page shows what was just
                // saved instead of reverting to the values it loaded.
                featureFlags = featureFlags?.let { it.copy(values = it.values + updates) }
                updates = emptyMap()
                drafts = emptyMap()
                description = null
                aggregatorService.notifications.info("Feature flags updated. Please wait up to a minute to allow them to propagate.")
            } catch (e: Throwable) {
                aggregatorService.notifications.error("Feature flags didn't update: ${e.message}")
            }
        }
    }

    fun save() {
        showConfirmation = true
    }

    val hasInvalidDrafts = drafts.values.any { it.error != null }

    waitUntilNonNull(featureFlags) { flags ->
        val flagsAndValues = flags.flags.map { (key, value) ->
            val updatedValue = updates[key] ?: flags.values[key]
            FlagsAndValues(key, value, updatedValue as? JsonObject)
        }
        box({
            title = "Current Feature Flags"
            paddedContent = false
            if (updates.isNotEmpty() || hasInvalidDrafts) {
                action {
                    title = "Save"
                    primary = true
                    showProgressOnAction = true
                    enabled = updates.isNotEmpty() && !hasInvalidDrafts
                    action {
                        save()
                    }
                }
                action {
                    title = "Reset"
                    action {
                        updates = emptyMap()
                        drafts = emptyMap()
                    }
                }
            }
        }) {
            table<FlagsAndValues> {
                items(flagsAndValues)

                column {
                    style {
                        property("vertical-align", "top")
                    }
                    width = 2.cssRem
                    content { flag ->
                        val isEnabled = flag.value?.get("enabled")?.jsonPrimitive?.booleanOrNull == true
                        Input(type = InputType.Checkbox) {
                            checked(isEnabled)
                            if (!editable) {
                                disabled()
                            }
                            onClick {
                                if (editable) {
                                    val updated = flag.value.copyOrBuild {
                                        put(ENABLED_KEY, !isEnabled)
                                    }
                                    updates = withFlagEdit(updates, flag.id, flags.values[flag.id], updated)
                                }
                            }
                        }
                    }
                }

                column {
                    content {
                        Div({
                            style {
                                fontWeight(700)
                                fontSize(14.px)
                            }
                        }) {
                            Text(it.descriptor.name)
                        }
                        it.descriptor.description?.let {
                            Div({ style { fontSize(.9.em) } }) {
                                Text(it)
                            }
                        }

                        val flag = it
                        flag.descriptor.attributes?.takeIf { attrs -> attrs.isNotEmpty() }?.let { attrs ->
                            Div({
                                style {
                                    marginTop(1.em)
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(0.75.em)
                                }
                            }) {
                                val savedValue = flags.values[flag.id] as? JsonObject
                                attrs.forEach { (attrKey, attribute) ->
                                    val ref = AttributeRef(flag.id, attrKey)
                                    AttributeEditor(
                                        attrKey = attrKey,
                                        attribute = attribute,
                                        savedValue = savedValue?.get(attrKey),
                                        currentValue = flag.value?.get(attrKey),
                                        draft = drafts[ref],
                                        editable = editable,
                                        onDraft = { draft -> drafts = drafts + (ref to draft) },
                                        onChange = { newValue ->
                                            val updated = flag.value.copyOrBuild {
                                                put(attrKey, newValue)
                                            }
                                            updates = withFlagEdit(updates, flag.id, flags.values[flag.id], updated)
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (showConfirmation) {
        confirmationDialogWithContent(
            title = "Confirm Feature Flag Changes",
            action = "Submit",
            confirmationText = "continue",
            cb = { confirmed ->
                if (confirmed) {
                    performSave()
                }
                showConfirmation = false
            }
        ) { _, _ ->
            Div({
                style {
                    marginBottom(1.em)
                }
            }) {
                Text("The following feature flags will be changed:")
            }

            featureFlags?.let { flags ->
                Div({
                    style {
                        backgroundColor(rgba(0, 0, 0, 0.1))
                        padding(10.px)
                        borderRadius(4.px)
                        marginBottom(1.em)
                    }
                }) {
                    updates.forEach { (key, value) ->
                        val flagName = flags.flags[key]?.name ?: key
                        val isEnabled = value[ENABLED_KEY]?.jsonPrimitive?.booleanOrNull == true
                        val changes = attributeChanges(
                            flags.values[key] as? JsonObject,
                            value,
                            flags.flags[key]?.attributes?.keys?.toList().orEmpty()
                        )
                        Div({
                            style {
                                padding(4.px, 0.px)
                            }
                        }) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    gap(8.px)
                                    property("align-items", "center")
                                }
                            }) {
                                Div({
                                    style {
                                        fontWeight(600)
                                    }
                                }) {
                                    Text(flagName)
                                }
                                Div({
                                    style {
                                        fontSize(12.px)
                                        padding(2.px, 8.px)
                                        borderRadius(3.px)
                                        if (isEnabled) {
                                            backgroundColor(SXMUI.messageSuccessBackground.value())
                                            color(SXMUI.messageSuccessForeground.value())
                                            border(1.px, LineStyle.Solid, SXMUI.messageSuccessBorder.value())
                                        } else {
                                            backgroundColor(SXMUI.messageAlertBackground.value())
                                            color(SXMUI.messageAlertForeground.value())
                                            border(1.px, LineStyle.Solid, SXMUI.messageAlertBorder.value())
                                        }
                                    }
                                }) {
                                    Text(if (isEnabled) "enabled" else "disabled")
                                }
                            }
                            AttributeChangeList(changes)
                        }
                    }
                }
            }

            dialogField("Description", "Describe the purpose of the update.") {
                simpleTextField(description) {
                    description = it
                }
            }
        }
    }
}

/**
 * Edits one attribute of a feature flag (UCA-979).
 *
 * Shows the attribute key, its declared type, an input chosen by [resolveAttributeEditorKind],
 * and an inline validation error when the typed text is invalid:
 * - `string`: a text field. Any text, including empty, is saved as a JSON string.
 * - `number`: a text field with a numeric keyboard. The text must be a finite number and is
 *   saved as a JSON number, never as a string. A plain text input (not `type=number`) is used so
 *   that invalid text stays visible next to its error instead of being cleared by the browser.
 * - `boolean`: a checkbox, saved as a JSON boolean.
 * - Anything else (missing or unknown type, or a value the typed widget cannot represent): a
 *   JSON text area that must hold one valid JSON value.
 *
 * The widget is chosen from the *saved* value so it does not change while the admin types.
 * Text inputs render [draft] text when there is one, so partial input such as `12.` is not
 * reformatted mid-edit. Every keystroke reports the new text through [onDraft]; only valid text
 * also reports a parsed value through [onChange]. When the current value differs from
 * [savedValue], a "modified" marker is shown next to the key.
 *
 * When [editable] is false the input is disabled but still shows the current value, so read-only
 * viewers can see attribute values of every type.
 *
 * @param attrKey The attribute's key within the flag's value object
 * @param attribute The attribute descriptor, which carries the declared constraint type
 * @param savedValue The value in the loaded document, used to pick the widget and detect changes
 * @param currentValue The value to show: the pending edit if there is one, otherwise the saved value
 * @param draft The text last typed into this attribute's field, if any
 * @param editable Whether the admin may change the value
 * @param onDraft Called with the typed text and its validation error on every input
 * @param onChange Called with the parsed JSON value whenever the input is valid
 */
@Composable
private fun AttributeEditor(
    attrKey: String,
    attribute: AppConfigAttribute,
    savedValue: JsonElement?,
    currentValue: JsonElement?,
    draft: AttributeDraft?,
    editable: Boolean,
    onDraft: (AttributeDraft) -> Unit,
    onChange: (JsonElement) -> Unit
) {
    val declaredType = attribute.constraints?.type
    val kind = resolveAttributeEditorKind(declaredType, savedValue)

    fun handleInput(text: String) {
        when (val result = parseAttributeInput(kind, text)) {
            is AttributeParseResult.Valid -> {
                onDraft(AttributeDraft(text, null))
                onChange(result.value)
            }

            is AttributeParseResult.Invalid -> onDraft(AttributeDraft(text, result.message))
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(4.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                property("align-items", "baseline")
                fontSize(13.px)
            }
        }) {
            Span({ style { fontWeight(600) } }) {
                Text(attrKey)
            }
            Span({ style { color(SXMUI.subtleTextColor.value()) } }) {
                Text(
                    when {
                        declaredType == null -> "no declared type"
                        kind == AttributeEditorKind.RawJson -> "${declaredType.name} (edited as JSON)"
                        else -> declaredType.name
                    }
                )
            }
            if (currentValue != savedValue) {
                Span({
                    style {
                        fontSize(11.px)
                        color(SXMUI.messageWarnForeground.value())
                    }
                }) {
                    Text("modified")
                }
            }
        }

        val text = draft?.text ?: attributeEditorText(kind, currentValue)
        when (kind) {
            AttributeEditorKind.Boolean -> {
                val checked = (currentValue as? JsonPrimitive)?.booleanOrNull == true
                Input(type = InputType.Checkbox) {
                    checked(checked)
                    if (!editable) {
                        disabled()
                    }
                    onClick {
                        if (editable) {
                            onChange(JsonPrimitive(!checked))
                        }
                    }
                }
            }

            AttributeEditorKind.RawJson -> {
                TextArea(text) {
                    style {
                        width(100.percent)
                        maxWidth(40.em)
                        minHeight(4.em)
                        fontFamily("monospace")
                        fontSize(13.px)
                        property("resize", "vertical")
                    }
                    if (!editable) {
                        disabled()
                    }
                    onInput { handleInput(it.value) }
                }
            }

            AttributeEditorKind.Text, AttributeEditorKind.Number -> {
                Input(InputType.Text) {
                    value(text)
                    style {
                        maxWidth(20.em)
                    }
                    if (kind == AttributeEditorKind.Number) {
                        attr("inputmode", "decimal")
                    }
                    if (!editable) {
                        disabled()
                    }
                    onInput { handleInput(it.value) }
                }
            }
        }

        draft?.error?.let { error ->
            Div({
                style {
                    fontSize(12.px)
                    color(SXMUI.messageAlertForeground.value())
                }
            }) {
                Text(error)
            }
        }
    }
}

/**
 * Lists attribute changes in the save confirmation dialog, one line per attribute, as
 * `key: old → new`. Values use [attributeSummaryText], so strings keep their quotes and a
 * number saved as a string is distinguishable from a real number. Renders nothing when
 * [changes] is empty (for example when only the enabled state changed).
 *
 * @param changes The attribute changes for one flag, from [attributeChanges]
 */
@Composable
private fun AttributeChangeList(changes: List<AttributeChange>) {
    if (changes.isEmpty()) return
    Div({
        style {
            marginTop(4.px)
            marginLeft(1.em)
            fontSize(13.px)
            fontFamily("monospace")
        }
    }) {
        changes.forEach { change ->
            Div {
                Text("${change.key}: ${attributeSummaryText(change.old)} \u2192 ${attributeSummaryText(change.new)}")
            }
        }
    }
}

@Serializable
data class FlagsAndValues(
    val id: String,
    val descriptor: AppConfigFeatureFlag,
    val value: JsonObject?
)
